// Core data types for AdGen Pro

export interface Project {
  id: string;
  name: string;
  source_table_name?: string; // Optional for backward compatibility
  created_at: string;
  updated_at: string;
}

// Generic data row - can represent any table structure.
// `id` is often null/undefined in source tables (custom imports, dynamic source tables) —
// callers must treat it as optional and prefer content-based equality where stability matters.
export interface DataRow {
  id: string | null;
  [key: string]: any; // Dynamic fields based on the source table
}

// Legacy City type - deprecated, use DataRow instead
export interface City {
  id: string;
  project_id: string;
  // Standard French commune fields
  nom_standard: string;
  nom_a: string;        // "à" form
  nom_de: string;       // "de" form
  nom_slug: string;
  region_nom: string;
  departement_code: string;
  departement_nom: string;
  population?: number;
  // Custom fields JSON
  custom_fields: Record<string, any>;
}


export type TemplateSetStatus = 'draft' | 'active' | 'archived';

export interface TemplateVariant {
  variant_order: number;
  template_string: string;
  enabled: boolean;
  notes?: string;
}

export interface HeadlineSlot {
  slot_number: number; // 1-15
  variants: TemplateVariant[];
  skip_if_too_long?: boolean; // if true, output empty string when no variant fits
  pinned_position?: 1 | 2 | 3; // pin to ad position 1, 2, or 3
}

export interface DescriptionSlot {
  slot_number: number; // 1-4
  variants: TemplateVariant[];
  skip_if_too_long?: boolean;
  pinned_position?: 1 | 2; // pin to ad position 1 or 2
}

export type KeywordMatchType = 'exact' | 'phrase' | 'broad';

export interface KeywordItem {
  id: string;
  item_name?: string;
  match_types: KeywordMatchType[];
  variants: TemplateVariant[];
}

export interface CalloutItem {
  id: string;
  variants: TemplateVariant[];
}

export interface SitelinkItem {
  id: string;
  sitelink_text: string;
  sitelink_url: string;
  description_1?: string;
  description_2?: string;
  enabled: boolean;
  display_order: number;
}

// Per-ad definition — each ad within a template has its own headlines, descriptions, and optional URLs
export interface AdDefinition {
  ad_index: number; // 1-based: 1, 2, or 3
  headlines: HeadlineSlot[];    // 15 slots
  descriptions: DescriptionSlot[]; // 4 slots
  // Per-ad URL overrides (only used when use_per_ad_urls is true)
  final_url_template?: string;
  path1_template?: string;
  path2_template?: string;
}

export interface TemplateSetContent {
  // Structure & Naming
  campaign_grouping_field?: string; // Deprecated - use primary_grouping_field; kept for back-compat
  primary_grouping_field?: string; // Primary grouping (e.g., 'departement_nom')
  secondary_grouping_field?: string; // Optional secondary grouping (e.g., 'custom:grade')
  campaign_name_template: string;
  ad_group_name_template: string;
  campaign_status: 'Enabled' | 'Paused';
  ad_group_status: 'Enabled' | 'Paused';

  // NEW: Selected fields for placeholder use throughout templates
  selected_fields?: string[]; // e.g., ['nom_standard', 'departement_nom', 'region_nom']

  // RSA Ad Copy — legacy single-ad fields (always kept in sync with ads[0])
  headlines: HeadlineSlot[];
  descriptions: DescriptionSlot[];

  // Multi-ad support (up to 3 RSAs per ad group)
  ads?: AdDefinition[];           // undefined/empty = single-ad legacy mode
  use_per_ad_urls?: boolean;      // default false = URLs shared across all ads

  // Keywords
  keywords: KeywordItem[];

  // URLs and Paths — shared by default
  final_url_template: string;
  path1_template: string;
  path2_template: string;

  // Extensions
  callouts: CalloutItem[];
  sitelinks: SitelinkItem[];

  // Template-scoped placeholders — fixed values applied to all rows during generation
  template_placeholders?: Record<string, string>;

  // Options
  generate_accent_stripped_keywords: boolean;
}

// Returns the effective ad definitions for a template, normalizing legacy single-ad format
export function getEffectiveAds(content: TemplateSetContent): AdDefinition[] {
  if (content.ads && content.ads.length > 0) return content.ads;
  return [{
    ad_index: 1,
    headlines: content.headlines,
    descriptions: content.descriptions,
  }];
}

export interface GenerationSettings {
  filters: Array<{
    field: string;
    operator: 'equals' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte';
    value: string | number;
  }>;
}

export interface TemplateSet {
  id: string;
  project_id: string;
  name: string;
  language_code: string;
  status: TemplateSetStatus;
  generation_settings: GenerationSettings;
  template_content: TemplateSetContent;
  created_at: string;
  updated_at: string;
  duplicated_from_template_set_id?: string;
}

export interface FallbackUsage {
  element_type: string;
  slot_or_item_id: string;
  variant_level: number;
  count: number;
}

export interface ValidationWarning {
  type: 'truncation' | 'missing_placeholder' | 'invalid_syntax' | 'char_limit';
  element_type: string;
  element_id: string;
  city_id?: string;
  message: string;
}

export interface GenerationLog {
  id: string;
  project_id: string;
  template_set_id: string | null;
  template_set_language_code: string;
  generated_at: string;
  grouping_field: string;
  filter_snapshot: GenerationSettings;
  totals: {
    campaigns: number;
    ad_groups: number;
    ads: number;
    keywords: number;
    callouts: number;
    sitelinks: number;
  };
  fallbacks_used: FallbackUsage[];
  warnings: ValidationWarning[];
  output_filename: string;
  output_blob?: string; // CSV content
}

export interface CustomGroup {
  id: string;
  name: string;
  campaignNameOverride?: string; // if set, overrides template campaign name for this group
  rows: DataRow[];
}

// Campaign structure override — used at generation time to override template defaults
export interface CampaignStructureOverride {
  primary_grouping_field?: string;
  secondary_grouping_field?: string;
  campaign_name_template?: string;
  ad_group_name_template?: string;
  campaign_status?: 'Enabled' | 'Paused';
  ad_group_status?: 'Enabled' | 'Paused';
}

// Campaign budget configuration
export interface BudgetConfig {
  enabled: boolean;
  mode: 'uniform' | 'per_campaign';
  uniformAmount?: number;
  perCampaignAmounts?: Record<string, number>; // campaign name → budget
}

export type MultiTemplateMode = 'separate' | 'merged';

// Generation preset — saved generation configuration
export interface GenerationPresetConfig {
  template_set_ids: string[];
  structure_override?: CampaignStructureOverride;
  override_enabled: boolean;
  bulk_enabled: boolean;
  filter_column?: string;
  filter_values?: string[];
  excluded_rows?: Record<string, any>[];
  budget_config?: BudgetConfig;
  custom_groups: Array<{
    name: string;
    campaign_name_override?: string;
    use_override: boolean;
    row_ids?: string[];
    row_data?: Record<string, any>[];
  }>;
}

export interface GenerationPreset {
  id: string;
  project_id: string;
  name: string;
  config: GenerationPresetConfig;
  created_at: string;
  updated_at: string;
}

// Character limits
export const CHARACTER_LIMITS = {
  headline: 30,
  description: 90,
  keyword: 80,
  callout: 25,
  sitelink_text: 25,
  sitelink_description: 35,
  display_path: 15,
  campaign_name: 128,
  ad_group_name: 255,
} as const;